import { Box, FormHelperText, CardHeader, Divider } from '@mui/material';
import { useLocation } from 'react-router-dom';
import Grid from '@mui/material/Grid2';

import type { FormikProps } from 'formik';

import { type TestProps, Test as TestModel, isLocationOnsite } from '../../../models';
import { TestSummary, TestSelector } from '../../../components/bulk-request';
import { TestSelectionSkeleton } from '../../../components/shared';
import { useGetBulkTestsQuery } from '../../../slices/apis/testApi';
import { useGetSuppliersQuery } from '../../../slices/apis/testingCentreApi';
import { useEffect } from 'react';

interface BulkRequestTestPickerProps {
	formik: FormikProps<{ tests: TestProps[] }>;
	allowQtyEditing: boolean;
}

export const BulkRequestTest: React.FC<BulkRequestTestPickerProps> = ({ formik, allowQtyEditing }) => {
	const { data } = useGetBulkTestsQuery();
	const { data: testingCentres } = useGetSuppliersQuery();
	const { pathname } = useLocation();

	const handleChange = (testId: TestProps['id'], value: number | null = 0) => {
		const qty = value ?? 0;
		formik.setFieldValue('tests', TestModel.updateTestQty(formik.values.tests, testId, qty));
	};

	const ifError = Boolean(formik.touched.tests) && Boolean(formik.errors.tests);

	// if new and onsite then, find travel fee and make it qty 1 and set min 1
	// else read formik.values.tests as it's edit
	// @ts-ignore
	// biome-ignore lint/correctness/useExhaustiveDependencies: include formik causes crash for unknown reason
	useEffect(() => {
		if (data && testingCentres && pathname.includes('new')) {
			// @ts-ignore
			const onsiteLocation = isLocationOnsite(testingCentres, formik.values.location);
			if (formik.values.tests.length === 0) {
				formik.setFieldValue('tests', TestModel.normaliseBulkTest(data, onsiteLocation));
			} else {
				formik.setFieldValue('tests', TestModel.normaliseBulkTest(formik.values.tests, onsiteLocation));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, testingCentres, pathname]);

	if (!data) {
		return <TestSelectionSkeleton />;
	}

	return (
		<>
			{ifError && <FormHelperText error>{formik.errors.tests as string}</FormHelperText>}
			<Grid container spacing={2} direction="row">
				{formik.values.tests.map((test) => {
					return (
						<Grid key={test.name}>
							<TestSelector
								test={test}
								key={test.name}
								onQtyChange={(val, event) => {
									allowQtyEditing && handleChange(test.id, val);
								}}
							/>
						</Grid>
					);
				})}
			</Grid>
			<CardHeader title="Summary" />
			<Divider sx={{ divider: { borderBottomWidth: 1.5 } }} />
			<Box sx={{ pt: 2 }}>
				<TestSummary tests={TestModel.removeNilTest(formik.values.tests)} />
			</Box>
		</>
	);
};
